import { defineStore } from 'pinia'
import type { ProductModel } from '@/types/catalog'
import type { Order } from '@/store/orders'
import { usePlausible } from '@/composables/usePlausible'

const LS_KEY = 'cart-v1'
const LS_ORDER_EDIT_KEY = 'cart-order-edit-v1'
const VERSION = 1

const { trackEvent } = usePlausible()

type WindowWithWisepops = typeof window & { wisepops?: ((...args: unknown[]) => void) }

const encodeFirestoreKey = (value: string | number) => encodeURIComponent(String(value))
const decodeFirestoreKey = (value: string) => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}
const getFirestoreInstance = async () => {
  const { $firestore } = useNuxtApp()
  if (!$firestore) return null
  return typeof $firestore === 'function' ? await $firestore() : $firestore
}

export interface CartLineItem {
  sku: string
  style: string | number
  colour: string
  size: string
  qty: number
}

interface PersistShape {
  v: number
  saved: (string | number)[]
  items: Record<string, { qty: number }>
  userEmail?: string
}

type FirestoreCart = Omit<PersistShape, 'v'> & { v?: number }

export const useCartStore = defineStore('cart', {
  state: () => ({
    hydrated: false,
    savedStylesArr: [] as (string | number)[],
    lineItems: {} as Record<string, CartLineItem>,
    currentOrderEdit: null as Order | null
  }),
  getters: {
    savedStyles(state) {
      return new Set<string | number>(state.savedStylesArr)
    },
    modelIsSaved: (state) => (style: string | number) => state.savedStylesArr.includes(style),
    totalModels: (state) => state.savedStylesArr.length,
    hasCartItems: (state) => Object.values(state.lineItems).some((li) => li.qty > 0)
  },
  actions: {
    toPersistShape(): PersistShape {
      const out: PersistShape = {
        v: VERSION,
        saved: Array.isArray(this.savedStylesArr) ? this.savedStylesArr.filter(v => v !== undefined && v !== null) : [],
        items: {}
      }
      for (const li of Object.values(this.lineItems)) {
        if (
          typeof li.sku === 'string' &&
          li.sku.trim().length &&
          typeof li.qty === 'number' &&
          !isNaN(li.qty)
        ) {
          out.items[li.sku] = { qty: li.qty }
        }
      }
      return out
    },
    applyPersistShape(data: FirestoreCart) {
      this.savedStylesArr = Array.isArray(data.saved) ? data.saved : []
      this.lineItems = {}
      for (const [sku, obj] of Object.entries(data.items || {})) {
        this.lineItems[sku] = {
          sku,
          style: '',
          colour: '',
          size: '',
          qty: obj.qty
        }
      }
      this.persist()
    },
    hydrate() {
      if (this.hydrated) return
      if (typeof window === 'undefined') return
      try {
        const raw = localStorage.getItem(LS_KEY)
        if (!raw) { this.hydrated = true; return }
        const parsed: PersistShape = JSON.parse(raw)
        if (parsed.v !== VERSION) {
          localStorage.removeItem(LS_KEY)
          this.hydrated = true
          return
        }
        this.savedStylesArr = Array.isArray(parsed.saved) ? parsed.saved : []
        for (const [sku, obj] of Object.entries(parsed.items || {})) {
          this.lineItems[sku] = {
            sku,
            style: '',
            colour: '',
            size: '',
            qty: obj.qty
          }
        }
      } catch {
        // ignore
      } finally {
        this.hydrated = true
      }
      this.hydrateOrderEdit()
    },
    persist() {
      if (typeof window === 'undefined') return
      const out = this.toPersistShape()
      localStorage.setItem(LS_KEY, JSON.stringify(out))
      this.persistToFirestoreDebounced()
    },
    ensureModelLineItems(model: ProductModel) {
      for (const v of model.variants) {
        if (!v.SKU) continue
        if (!this.lineItems[v.SKU]) {
          this.lineItems[v.SKU] = {
            sku: v.SKU,
            style: model.Style,
            colour: v.Colour,
            size: v.Size,
            qty: 0
          }
        } else {
          const li = this.lineItems[v.SKU]
          li.style = model.Style
          li.colour = v.Colour
          li.size = v.Size
        }
      }
    },
    toggleModelSave(model: ProductModel) {
      const idx = this.savedStylesArr.indexOf(model.Style)
      if (idx !== -1) {
        this.savedStylesArr.splice(idx, 1)
      } else {
        this.savedStylesArr.push(model.Style)
        this.ensureModelLineItems(model)
        trackEvent('Save to Cart', { 
          style: String(model.Style),
          productName: model.name
        })
      }
      this.persist()
    },
    setVariantQty(sku: string, qty: number) {
      const li = this.lineItems[sku]
      if (!li) return
      li.qty = Math.max(0, Math.floor(qty))
      this.persist()
    },
    inc(sku: string) { 
      const li = this.lineItems[sku]
      if (li) {
        const oldQty = li.qty
        li.qty++
        if (oldQty === 0) {
          trackEvent('Add to Cart', {
            sku: sku,
            style: String(li.style),
            colour: li.colour,
            size: li.size,
            quantity: li.qty
          })
          if (typeof window !== 'undefined') {
            const windowWithWisepops = window as WindowWithWisepops
            windowWithWisepops.wisepops?.('event', 'addToCart', {
              sku,
              style: String(li.style),
              colour: li.colour,
              size: li.size,
              quantity: li.qty
            })
          }
        }
        this.persist()
      }
    },
    dec(sku: string) { 
      const li = this.lineItems[sku]
      if (li) { li.qty = Math.max(0, li.qty - 1); this.persist() }
    },
    clearVariant(sku: string) { 
      const li = this.lineItems[sku]
      if (li) { li.qty = 0; this.persist() }
    },
    clearColour(style: string | number, colour: string) {
      for (const li of Object.values(this.lineItems)) {
        if (li.style === style && li.colour === colour) li.qty = 0
      }
      this.persist()
    },
    clearModelQuantities(style: string | number) {
      for (const li of Object.values(this.lineItems)) {
        if (li.style === style) li.qty = 0
      }
      this.persist()
    },
    clearAllSavedStyles() {
      this.savedStylesArr = []
      this.persist()
    },
    addSavedStyle(style: string | number) {
      if (!this.savedStylesArr.includes(style)) {
        this.savedStylesArr.push(style)
        this.persist()
      }
    },
    // --- Order Edit Management ---
    hydrateOrderEdit() {
      if (typeof window === 'undefined') return
      try {
        const raw = localStorage.getItem(LS_ORDER_EDIT_KEY)
        if (raw) {
          this.currentOrderEdit = JSON.parse(raw)
        }
      } catch {
        // ignore
      }
    },
    persistOrderEdit() {
      if (typeof window === 'undefined') return
      try {
        if (this.currentOrderEdit) {
          localStorage.setItem(LS_ORDER_EDIT_KEY, JSON.stringify(this.currentOrderEdit))
        } else {
          localStorage.removeItem(LS_ORDER_EDIT_KEY)
        }
      } catch {
        // ignore
      }
    },
    setOrderEdit(order: Order) {
      this.currentOrderEdit = { ...order }
      this.persistOrderEdit()
    },
    clearOrderEdit() {
      this.currentOrderEdit = null
      this.persistOrderEdit()
    },
    // --- Firestore sync ---
    async fetchFirestoreCart(userUidOrEmail: string): Promise<FirestoreCart | null> {
      try {
        const firestore = await getFirestoreInstance()
        if (!firestore) {
          console.error('No $firestore found in Nuxt app!')
          return
        }
        const { doc, getDoc, collection } = await import('firebase/firestore')
        const cartDoc = doc(collection(firestore, 'carts'), userUidOrEmail)
        const snap = await getDoc(cartDoc)
        if (!snap.exists()) {
          return null
        }
        const data = snap.data()
        const normalizedItems: Record<string, { qty: number }> = {}
        for (const [sku, obj] of Object.entries(data.items || {})) {
          const decodedSku = decodeFirestoreKey(sku)
          normalizedItems[decodedSku] = { qty: obj.qty }
        }
        return {
          v: typeof data.v === 'number' ? data.v : VERSION,
          saved: Array.isArray(data.saved) ? data.saved : [],
          items: normalizedItems,
          userEmail: typeof data.userEmail === 'string' ? data.userEmail : ''
        }
      } catch (err) {
        console.error('fetchFirestoreCart error:', err)
        if (process.client && typeof useToast === 'function') {
          useToast().add({
            title: 'Cart sync failed',
            description: 'Could not load cart from server. Using local cart.',
            color: 'error',
            icon: 'i-lucide-alert-triangle'
          })
        }
        return null
      }
    },
    async hydrateFromFirestore(userUidOrEmail: string) {
      const remoteCart = await this.fetchFirestoreCart(userUidOrEmail)
      if (!remoteCart) return
      this.applyPersistShape(remoteCart)
    },
    async persistToFirestore(userUidOrEmail?: string) {
      try {
        const firestore = await getFirestoreInstance()
        if (!firestore) {
          console.error('No $firestore found in Nuxt app!')
          return
        }
        let userEmail = ''
        if (!userUidOrEmail) {
          try {
            const { useAuthStore } = await import('@/store/auth')
            const auth = useAuthStore()
            userUidOrEmail = auth.user?.uid || auth.user?.email
            userEmail = auth.user?.email || ''
            if (!userUidOrEmail) {
              console.error('persistToFirestore: No userUidOrEmail found!')
              return
            }
          } catch (err) {
            console.error('persistToFirestore: Could not import/use auth:', err)
          }
        } else {
          try {
            const { useAuthStore } = await import('@/store/auth')
            const auth = useAuthStore()
            userEmail = auth.user?.email || ''
          } catch (err) {
            console.error('persistToFirestore: Could not import/use auth:', err)
          }
        }
        
        const { doc, setDoc, collection } = await import('firebase/firestore')
        const cartDoc = doc(collection(firestore, 'carts'), userUidOrEmail)
        const out: PersistShape = this.toPersistShape()
        const encodedItems: Record<string, { qty: number }> = {}
        for (const [sku, obj] of Object.entries(out.items)) {
          encodedItems[encodeFirestoreKey(sku)] = obj
        }
        out.items = encodedItems
        out.userEmail = userEmail
        // LOG PAYLOAD!
        //console.log('Firestore payload:', JSON.stringify(out, null, 2))
        await setDoc(cartDoc, out)
      } catch (err) {
        console.error('persistToFirestore error:', err)
        if (process.client && typeof useToast === 'function') {
          useToast().add({
            title: 'Cart sync failed',
            description: 'Could not save cart to server.',
            color: 'error',
            icon: 'i-lucide-alert-triangle'
          })
        }
      }
    },
    persistToFirestoreDebounced: (() => {
      let timeout: any = null
      return function(this: any) {
        if (timeout) clearTimeout(timeout)
        timeout = setTimeout(async () => {
          const { useAuthStore } = await import('@/store/auth')
          const auth = useAuthStore()
          if (auth.user?.uid || auth.user?.email) {
            await this.persistToFirestore(auth.user?.uid || auth.user?.email)
          } else {
            console.error('persistToFirestoreDebounced: No user!')
          }
        }, 600)
      }
    })()
  }
})
